import { forwardRef } from 'react'
import * as RadixCheckbox from '@radix-ui/react-checkbox'
import { Check, Minus } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import { Text } from './Typography.js'

export interface CheckboxProps extends React.ComponentPropsWithoutRef<typeof RadixCheckbox.Root> {
  label?: React.ReactNode
  /** Under the label. What ticking it actually does. */
  description?: React.ReactNode
}

/**
 * A box, and the words that make it worth ticking.
 *
 * The label is part of the component because a checkbox whose label is a sibling
 * `<span>` is a checkbox you cannot tick by pressing the words — which is most of
 * the target on a desk screen.
 */
export const Checkbox = forwardRef<React.ElementRef<typeof RadixCheckbox.Root>, CheckboxProps>(
  function Checkbox({ className, label, description, id, ...props }, ref) {
    const box = (
      <RadixCheckbox.Root
        ref={ref}
        id={id}
        className={cn(
          'peer size-5 shrink-0 rounded-sm border border-hairline-strong bg-surface',
          'outline-none transition-colors',
          'focus-visible:ring-2 focus-visible:ring-coral-fill/30',
          'data-[state=checked]:bg-coral-fill data-[state=checked]:border-coral-fill',
          'data-[state=indeterminate]:bg-coral-fill data-[state=indeterminate]:border-coral-fill',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className,
        )}
        {...props}
      >
        <RadixCheckbox.Indicator className="flex items-center justify-center text-on-coral">
          {props.checked === 'indeterminate' ? (
            <Minus className="size-3.5" />
          ) : (
            <Check className="size-3.5" />
          )}
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>
    )
    if (!label) return box
    return (
      <label className="flex cursor-pointer items-start gap-3">
        {box}
        <span className="flex flex-col gap-0.5">
          <Text as="span" variant="body">
            {label}
          </Text>
          {description && (
            <Text as="span" variant="caption">
              {description}
            </Text>
          )}
        </span>
      </label>
    )
  },
)
